import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Eye, EyeOff, LogIn } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { auth, login, loginGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [voir, setVoir] = useState(false);
  const [erreur, setErreur] = useState("");
  const [chargement, setChargement] = useState(false);

  if (auth) return <Navigate to="/" replace />;

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur("");
    setChargement(true);
    try {
      await login(email, password);
    } catch (err) {
      setErreur(apiErr(err));
    } finally {
      setChargement(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="page-login">
      <div className="hidden lg:flex flex-col justify-between w-[44%] p-12" style={{ background: "#14213D" }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-[12px] flex items-center justify-center font-extrabold text-white"
            style={{ background: "linear-gradient(105deg, #14213D 52%, #E5232E 52%)", border: "1px solid rgba(255,255,255,0.2)" }}>ID</div>
          <div>
            <div className="text-white font-bold text-[20px] leading-tight">ID Core</div>
            <div className="text-[10px] tracking-[0.25em] text-[#8FA3C0] font-semibold">DMS<span className="inline-block w-7 border-b-2 border-[#E5232E] ml-1 align-middle" /></div>
          </div>
        </div>
        <div>
          <h1 className="text-white text-3xl font-extrabold leading-snug">Le DMS qui connecte<br />atelier, commerce et clients.</h1>
          <p className="text-[#8FA3C0] mt-4 text-[14px] max-w-md">Planning atelier, ordres de réparation, stock véhicules, pipeline commerce, agenda Microsoft 365 et ID Copilot — dans un seul outil.</p>
        </div>
        <div className="text-[#5B6B84] text-[12px]">© 2026 ID Core — Groupe Kroely</div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-[#F6F7F9] px-6">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-[10px] flex items-center justify-center font-extrabold text-white text-sm"
              style={{ background: "linear-gradient(105deg, #14213D 52%, #E5232E 52%)" }}>ID</div>
            <span className="font-bold text-[17px]">ID Core</span>
          </div>
          <h2 className="text-lg font-bold" data-testid="login-titre">Connexion</h2>
          <p className="text-[13px] text-[--idc-muted] mt-1 mb-6">Accédez à votre espace de travail.</p>

          <form onSubmit={soumettre} className="space-y-3.5">
            <div>
              <label className="text-[12px] font-semibold text-[--idc-muted]">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 bg-white border border-[--idc-border] rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2F6FED]/30"
                placeholder="prenom.nom@idcore.fr" data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-[--idc-muted]">Mot de passe</label>
              <div className="relative">
                <input type={voir ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full mt-1 bg-white border border-[--idc-border] rounded-lg px-3.5 py-2.5 pr-10 text-sm outline-none focus:ring-2 focus:ring-[#2F6FED]/30"
                  placeholder="••••••••" data-testid="login-password-input" />
                <button type="button" onClick={() => setVoir(!voir)} className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-[--idc-muted]" data-testid="login-voir-mdp-btn">
                  {voir ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {erreur && <div className="text-[12.5px] text-[#B91C1C] bg-[#FEE2E2] rounded-lg px-3 py-2" data-testid="login-error">{erreur}</div>}
            <Button type="submit" disabled={chargement} className="w-full bg-[#16326E] hover:bg-[#122a5c] h-10" data-testid="login-submit-btn">
              <LogIn className="w-4 h-4 mr-2" /> {chargement ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 border-t border-[--idc-border]" />
            <span className="text-[11px] text-[--idc-muted]">ou</span>
            <div className="flex-1 border-t border-[--idc-border]" />
          </div>
          <Button variant="outline" className="w-full h-10" onClick={loginGoogle} data-testid="login-google-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 12 1 11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
            Se connecter avec Google
          </Button>
        </div>
      </div>
    </div>
  );
}
